// client/src/features/quotations/unitChangeTarget.ts
//
// The Unit dropdown's target price (D85), moved out of QuotationDetailPage. Lives in
// features/ because it reads `anchorFromItem`; utils/ must not depend on features/.

import type { QuotationItemDto } from '../../lib/quotationApi';
import { positiveNumber } from '../../utils/priceUtils';
import { markupCostUsd } from '../../utils/pricing';
import { LITRE_PER_UNIT, convertUnitPrice, normalizePackStructure } from '../../utils/packConversion';
import { canonicalUnit } from '../../utils/unitVocabulary';
import { deriveRatio } from '../../utils/unitConversionGraph';
import {
  KG_UNITS,
  LB_UNITS,
  barredByWater,
  establishedUnits,
  getAlternateQuotationPricing,
  isEquivalentUnitFamily,
  parsePackSizeFromDescription,
} from '../../utils/quotationUnitConversions';
import { anchorFromItem, normalizeUnit, type ConversionAnchor } from './linePriceApplication';

const OZ_UNITS = new Set(['OZ', 'OZS', 'OUNCE', 'OUNCES']);
const LITRE_UNITS = new Set(['L', 'LT', 'LTR', 'LTRS', 'LITRE', 'LITRES', 'LITER', 'LITERS']);
const PIECE_UNITS = new Set(['PCS', 'PCE', 'EA', 'EACH', 'UNIT', 'UNITS', 'INNER']);
const OUTER_UNITS = new Set(['TIN', 'CASE', 'CARTON', 'BOX', 'PACK', 'BAG', 'BUNDLE', 'TRAY']);
const OZ_PER_LB = 16;

export const readPackStructure = (item: QuotationItemDto): unknown => {
  const pd = item.pricingDetails as Record<string, unknown> | undefined | null;
  const de = pd && typeof pd === 'object' ? (pd.displayEquivalents as Record<string, unknown> | undefined) : undefined;
  return de && typeof de === 'object' ? de.packStructure ?? null : null;
};

export const readConversionAnchor = (item: QuotationItemDto): ConversionAnchor | null => {
  const pd = item.pricingDetails as Record<string, unknown> | undefined | null;
  const a = pd && typeof pd === 'object' ? (pd.conversionAnchor as Record<string, unknown> | undefined) : undefined;
  if (!a || typeof a !== 'object') return null;
  const unitPrice = positiveNumber(a.unitPrice);
  const uom = typeof a.uom === 'string' && a.uom.trim() ? a.uom.trim() : null;
  if (unitPrice == null || !uom) return null;
  return {
    uom,
    unitPrice,
    quantity: positiveNumber(a.quantity),
    unitSellingPrice: positiveNumber(a.unitSellingPrice),
    competitorPrice: positiveNumber(a.competitorPrice),
  };
};

/**
 * The US$ cost per `targetUnit` a Unit change restates a line to, or null when
 * nothing can answer. UNROUNDED in every branch (D85): the caller stores it as
 * `unitPriceExact` and ceils it once for `unitPrice`; ceiling it here too rounded
 * the selling price up twice (TT$2.00 PCS -> CASE gave 5.22, not 5.21).
 */
export const targetUnitPriceFor = (
  item: QuotationItemDto,
  targetUnit: string,
  altPricing: ReturnType<typeof getAlternateQuotationPricing>
): number | null => {
  const currentUnitPrice = typeof item.unitPrice === 'number' ? item.unitPrice : parseFloat(String(item.unitPrice || 0)) || 0;
  const currentUom = normalizeUnit(item.uom || 'PCS');
  const normalizedTarget = normalizeUnit(targetUnit);
  if (isEquivalentUnitFamily(currentUom, normalizedTarget)) return markupCostUsd(currentUnitPrice, item.unitPriceExact);
  // D170.4: asked or established in a volume, never a weight through the ounce, and the reverse.
  if (barredByWater(establishedUnits(item.originalRequest?.uom, item.pricingDetails, item.uom), normalizedTarget)) return null;

  // The pack structure, from the ANCHOR — the price the user last actually
  // established, not whatever this line currently shows (converting off the
  // current price ratchets a cent per hop). It is also the only path that
  // answers in both directions: PCS -> CASE -> PCS lands back on the original.
  const pack = readPackStructure(item);
  if (pack) {
    const anchor = readConversionAnchor(item) || anchorFromItem(item);
    if (anchor) {
      const converted = convertUnitPrice({
        price: anchor.unitPrice,
        fromUom: anchor.uom,
        toUom: normalizedTarget,
        pack,
      });
      if (converted != null && converted > 0) return converted;
    }
  }

  // A MEASURE to a measure (KG, LB, OZ, L, GAL...) needs no pack: the fixed ratio the quantity
  // restates by (`deriveRatio`, the same owner) applied to the ANCHOR's unrounded price. The
  // `altPricing` figures below are ceiled to the cent, and an ounce is 1/35 of a kilo: off the
  // per-lb cent, KG -> OZ cost US$0.034375 where 1.20 / 35.36 is 0.03394, so 884 OZ quoted
  // US$35.36 for US$30.00 of rice, and an OZ line had no per-kg figure to go back with (B001).
  const measureAnchor = readConversionAnchor(item) || anchorFromItem(item);
  if (measureAnchor) {
    // An ounce crosses to a volume at the pack's litres per ounce, the figure the quantity
    // restates by (`restateQuantityThroughPack`), else by water (D170.4).
    const ounce = { litrePerOunce: normalizePackStructure(pack).litrePerOunce };
    const perTarget = deriveRatio(null, measureAnchor.uom, normalizedTarget, ounce);
    if (perTarget != null && perTarget > 0) return measureAnchor.unitPrice / perTarget;
  }

  if (KG_UNITS.has(normalizedTarget) && altPricing.perKgUsd != null) return altPricing.perKgUsd;
  if (LB_UNITS.has(normalizedTarget) && altPricing.perLbUsd != null) return altPricing.perLbUsd;
  // Ounces come off the POUND figure (sixteen to the pound), not a second table.
  if (OZ_UNITS.has(normalizedTarget) && altPricing.perLbUsd != null) return altPricing.perLbUsd / OZ_PER_LB;
  if (LITRE_UNITS.has(normalizedTarget) && altPricing.perLitreUsd != null) return altPricing.perLitreUsd;
  // A gallon is four litres (the owner's, 2026-09-14), off the per-litre figure.
  if (canonicalUnit(normalizedTarget) === 'GAL' && altPricing.perLitreUsd != null) {
    return altPricing.perLitreUsd * LITRE_PER_UNIT.GAL;
  }

  if (OUTER_UNITS.has(normalizedTarget)) {
    // The reference row's own outer pack, gated on the NAME matching: "10 to a
    // CASE" says nothing about a BOX (QUO-00010).
    if (
      altPricing.perOuterUsd != null &&
      altPricing.outerUomLabel &&
      normalizeUnit(altPricing.outerUomLabel) === normalizedTarget
    ) {
      return altPricing.perOuterUsd;
    }
    const descPack = parsePackSizeFromDescription(item.itemName || item.itemDescription || '');
    if (descPack?.count && altPricing.perInnerUsd != null) return altPricing.perInnerUsd * descPack.count;
    if (descPack?.totalKgPerOuter && altPricing.perKgUsd != null) return altPricing.perKgUsd * descPack.totalKgPerOuter;
  }

  if (PIECE_UNITS.has(normalizedTarget) && altPricing.perInnerUsd != null) return altPricing.perInnerUsd;

  return null;
};
